import { useEffect, useRef, useState } from 'react';

// Reduced from 1000ms to improve responsiveness for low-memory systems
const VIEW_SETTLE_DEBOUNCE_MS = 500;
const MIN_INTERACTION_DURATION_MS = 150;

/**
 * Tracks whether supplemental GIS layers should be paused during pan/zoom.
 *
 * @description ArcGIS FeatureLayers query the server on every view extent change,
 * causing excessive API calls during pan/zoom. This hook returns a boolean indicating
 * whether layers should be temporarily hidden. The actual visibility control should
 * happen in the layer rendering logic (Map.js) to avoid conflicts.
 *
 * @param mapInstance - The ESRI MapView instance from the map component
 * @returns boolean - true when supplemental layers should be hidden (during interaction)
 */
export function useGISLayersViewDebounce(
  mapInstance: __esri.MapView | undefined,
): boolean {
  const [shouldPauseLayers, setShouldPauseLayers] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInteractingRef = useRef(false);

  const view = (mapInstance as { view?: __esri.MapView } | undefined)?.view;

  useEffect(() => {
    if (!view) return;

    const interactingHandle = view.watch(
      'interacting',
      (isInteracting: boolean) => {
        if (isInteracting && !isInteractingRef.current) {
          isInteractingRef.current = true;
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
          }
          hideDelayTimerRef.current = setTimeout(() => {
            if (isInteractingRef.current) {
              setShouldPauseLayers(true);
            }
          }, MIN_INTERACTION_DURATION_MS);
        }
      },
    );

    const stationaryHandle = view.watch(
      'stationary',
      (isStationary: boolean) => {
        if (isStationary && isInteractingRef.current) {
          isInteractingRef.current = false;
          if (hideDelayTimerRef.current) {
            clearTimeout(hideDelayTimerRef.current);
            hideDelayTimerRef.current = null;
          }
          debounceTimerRef.current = setTimeout(() => {
            setShouldPauseLayers(false);
          }, VIEW_SETTLE_DEBOUNCE_MS);
        }
      },
    );

    return () => {
      interactingHandle.remove();
      stationaryHandle.remove();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (hideDelayTimerRef.current) clearTimeout(hideDelayTimerRef.current);
    };
  }, [view]);

  return shouldPauseLayers;
}
